import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { TransitionGroup } from 'react-transition-group';
import CssTransition from '../../components/Transition/CssTransition';
import FindLocalRepPopup from '../../components/LocalRep/FindLocalRepPopup';

import { getActionCompletedTime } from '../../utils/actionTiming';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { CardTypes } from '../../utils/enums';
import { handleSkippingEndCard } from '../../utils/handleSkippingEndCard';
import { copyFor, capitalization } from '../../copy/copy';

import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPartner from '../Widget/OverlayPartner';

import WidgetHeader from '../Widget/WidgetHeader';
import RoundImage from '../../components/Image/RoundImage';

import Button from '../../components/Button';
import TwoGroup from '../../components/TwoGroup';

import { isPortrait, widthEm } from '../../utils/size';
import { isLongText } from '../../components/Shared/commonFunctions';
import defaultImage from '../../assets/images/localRepDefault.jpg';

import { onBackWithinCard, onGoToNextCard } from '../Widget/actions';
import { onCallComplete, onContainerClick } from './actions';

import { onFindYourRep, hideFindRepPopup, loadLocalRep } from '../LocalRep/actions';

import {
    Container,
    Body as DefaultBody,
    Content as DefaultContent,
    ContentContainer,
    Sidebar,
    FindRepLink,
    ImageContainer,
    PhoneNumber,
    SidebarTitle,
    ContentContainerMobile,
    ImageContainerMobile,
    ContentInnerMobile,
    ContentTitle,
    ContentDescription,
    ButtonGroup
} from '../DefaultStyles/Details';

import { LoadingSpinner } from '../../components/LoadingSpinner';

const Body = DefaultBody.extend`
    margin: 1.25em 1em 1em;
`;

const Content = DefaultContent.extend`
    margin-top: ${props => (props.isMobileResolution ? '1.5em' : '0.875em')};
    ${() => (widthEm() <= 30 ? `flex-direction: column` : ``)};
`;

const RepContainer = styled.div`
    text-align: center;
    margin-top: 22px;
    padding: 0 28px;
    float: left;
    width: ${props =>
        props.senatorsNotAvailable
            ? '100%'
            : props =>
                  props.showOnlySenators
                      ? props.oneSenatorAvailable
                          ? 'calc(100%)'
                          : 'calc(100% / 2)'
                      : props.oneSenatorAvailable
                      ? 'calc(100% / 2)'
                      : 'calc(100% / 3)'};
    div {
        margin: 0 auto;
    }

    ${() =>
        widthEm() <= 30 &&
        `
            width: 100%;
            padding: 0;
            text-align: left;

            > div {
                width: 80px;
                float: left;
                margin-right: 14px;

                img {
                width: 80px;
                height: 80px;`};
`;

const RepTitle = styled.h2`
    font-family: Averta;
    font-size: 16px;
    font-weight: bold;
    line-height: 1.25;
    margin-top: 12px;
    margin-bottom: 6px;

    @media (max-width: 30em) {
        margin-top: 0;
    }
`;

const TipsHeader = styled.h2`
    margin: 0;
    font-size: ${props => (isPortrait() ? (props.isLongText ? 17 : 20) : 22)}px;
    line-height: 1.25;
    padding-bottom: 10px;
`;

const Tip = styled.div`
    font-weight: normal;
    font-size: ${() => (isPortrait() ? 14 : 16)}px;
    line-height: 1.25;
`;

const NotYourRepInfo = styled.div`
    font-family: Lato;
    font-size: 0.75em;
    line-height: 1.33;
    font-weight: bold;

    ${() =>
        !isPortrait() &&
        `
            z-index: 2;
            position: absolute;
            bottom: 15px;
            left: 20px;
            width: 150px;`};
`;

const getButtonLabel = isSinglePerson => {
    return !isSinglePerson
        ? copyFor('buttons', 'finished', 'call')
        : copyFor('buttons', 'submit', 'call');
};

const normalizePhone = number => {
    return number ? '+1' + number.replace(/\D/g, '') : '';
};

const SingleCallDetails = props => (
    <Content isMobileResolution={props.isMobileResolution}>
        <Sidebar>
            <ImageContainer>
                <RoundImage
                    src={
                        (props.isHouseRep ? props.image : props.pictureUrl) ||
                        props.pictureUrl ||
                        defaultImage
                    }
                />
            </ImageContainer>
            <SidebarTitle>
                {copyFor('sidebar', 'title', 'call', undefined, {
                    recipientName: props.recipientName
                })}
            </SidebarTitle>
            <PhoneNumber target="_parent" href={`tel:${props.normalizedPhone}`}>
                {props.phone}
            </PhoneNumber>
        </Sidebar>
        <ContentContainer>
            <ContentContainerMobile>
                <ImageContainerMobile>
                    <RoundImage
                        isLongText={isLongText(props.recipientName, 20)}
                        src={
                            (props.isHouseRep ? props.image : props.pictureUrl) ||
                            props.pictureUrl ||
                            defaultImage
                        }
                    />
                </ImageContainerMobile>
                <ContentInnerMobile>
                    <ContentTitle isHouseRep={props.isHouseRep}>
                        Call {props.recipientName}
                        <br />
                        <PhoneNumber
                            target="_parent"
                            href={`tel:${props.normalizedPhone}`}
                            onClick={() => {
                                if (props.skipEndCard) {
                                    props.handleSkippingEndCard(
                                        props.nextAction,
                                        props.cards,
                                        props.onGoToNextCard
                                    );
                                }

                                props.onCallComplete(
                                    props.apiRequestData,
                                    props.actionCompletedTime(),
                                    props.isMultiActionsCard,
                                    props.actionIdentifier,
                                    props.thankYouText,
                                    props.thankYouTitle,
                                    true,
                                    props.localRepresentativeState,
                                    props.useLocalRepresentative,
                                    props.recipientName,
                                    props.skipEndCard
                                );
                            }}
                        >
                            {props.phone}
                        </PhoneNumber>
                    </ContentTitle>
                    {props.isHouseRep && (
                        <NotYourRepInfo>
                            {copyFor('findReps', 'notYour', 'call')} <br />
                            <FindRepLink onClick={props.onFindYourRep}>
                                {copyFor('findReps', 'clickToFind', 'call')}
                            </FindRepLink>
                        </NotYourRepInfo>
                    )}
                </ContentInnerMobile>
            </ContentContainerMobile>
            <ContentDescription>
                <TipsHeader isLongText={isLongText(props.heading, 60)}>
                    {props.heading}
                </TipsHeader>
                {props.tips.map(tip => (
                    <Tip key={tip}>{tip}</Tip>
                ))}
            </ContentDescription>
        </ContentContainer>
    </Content>
);
SingleCallDetails.propTypes = {
    actionIdentifier: PropTypes.string.isRequired,
    image: PropTypes.string,
    pictureUrl: PropTypes.string,
    isMobileResolution: PropTypes.bool.isRequired,
    recipientName: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    normalizedPhone: PropTypes.string.isRequired,
    tips: PropTypes.array.isRequired,
    onGoToNextCard: PropTypes.func.isRequired,
    handleSkippingEndCard: PropTypes.func.isRequired,
    onCallComplete: PropTypes.func.isRequired,
    actionCompletedTime: PropTypes.func.isRequired,
    apiRequestData: PropTypes.object.isRequired,
    isMultiActionsCard: PropTypes.bool.isRequired,
    onFindYourRep: PropTypes.func.isRequired,
    thankYouText: PropTypes.string.isRequired,
    thankYouTitle: PropTypes.string.isRequired,
    localRepresentativeState: PropTypes.number.isRequired,
    isHouseRep: PropTypes.bool.isRequired,
    useLocalRepresentative: PropTypes.bool,
    skipEndCard: PropTypes.bool,
    heading: PropTypes.string.isRequired
};

const SenatorsOrAllCallDetails = props => (
    <Content>
        {props.representatives.map(a => (
            <RepContainer
                key={a.phone}
                showOnlySenators={props.showOnlySenators}
                oneSenatorAvailable={props.oneSenatorAvailable}
                senatorsNotAvailable={props.senatorsNotAvailable}
            >
                <ImageContainer>
                    <RoundImage src={a.photoUrl} size="100px" />
                </ImageContainer>
                <RepTitle>{a.fullName}</RepTitle>
                <PhoneNumber target="_parent" href={`tel:${a.normalizedPhone}`}>
                    {a.phone}
                </PhoneNumber>
            </RepContainer>
        ))}
    </Content>
);
SenatorsOrAllCallDetails.propTypes = {
    representatives: PropTypes.array.isRequired,
    showOnlySenators: PropTypes.bool.isRequired,
    senatorsNotAvailable: PropTypes.bool,
    oneSenatorAvailable: PropTypes.bool
};

const CallDetails = props => (
    <Container onClick={() => props.onContainerClick(props.actionIdentifier)}>
        <Overlay />
        <OverlayPublisher />
        <OverlayPartner transitionType="right-to-left" />
        <Body>
            <WidgetHeader />
            {props.isSinglePerson
                ? SingleCallDetails(props)
                : SenatorsOrAllCallDetails(props)}
            {!!props.localRepresentativeState &&
                (!props.isMobileResolution || !props.isHouseRep) && (
                    <NotYourRepInfo>
                        {copyFor('findReps', 'notYour', 'call')}
                        {props.isMobileResolution ? <span>&nbsp;</span> : <br />}
                        <FindRepLink onClick={props.onFindYourRep}>
                            {copyFor('findReps', 'clickToFind', 'call')}
                        </FindRepLink>
                    </NotYourRepInfo>
                )}
            <ButtonGroup
                skipFrontCard={props.skipFrontCard}
                isSubmitting={props.isSubmitting}
                isPhoneLinkClicked={props.isPhoneLinkClicked}
                isSinglePerson={props.isSinglePerson}
            >
                <TwoGroup>
                    {!props.skipFrontCard && (
                        <Button
                            label={copyFor(
                                'buttons',
                                'back',
                                'call',
                                capitalization.eachWord
                            )}
                            onClick={() =>
                                props.onBackWithinCard(props.actionIdentifier, props.card)
                            }
                            className="middleButton"
                        />
                    )}
                    <Button
                        primary={true}
                        label={getButtonLabel(props.isSinglePerson)}
                        type="button"
                        className="submitButton"
                        normalizedPhone={
                            props.isSinglePerson ? props.normalizedPhone : ''
                        }
                        isMobileResolution={props.isMobileResolution}
                        isSubmitting={props.isSubmitting}
                        onClick={() => {
                            if (props.skipEndCard) {
                                props.handleSkippingEndCard(
                                    props.nextAction,
                                    props.cards,
                                    props.onGoToNextCard
                                );
                            }

                            props.onCallComplete(
                                props.apiRequestData,
                                props.actionCompletedTime(),
                                props.isMultiActionsCard,
                                props.actionIdentifier,
                                props.thankYouText,
                                props.thankYouTitle,
                                false,
                                props.localRepresentativeState,
                                props.useLocalRepresentative,
                                props.recipientName,
                                props.skipEndCard
                            );
                        }}
                    />
                    <LoadingSpinner />
                </TwoGroup>
            </ButtonGroup>
        </Body>
        <TransitionGroup>
            {props.showFindYouRepPopup && (
                <CssTransition key="alert" in={props.showFindYouRepPopup}>
                    <FindLocalRepPopup
                        hideFindRepPopup={props.hideFindRepPopup}
                        onSubmit={data =>
                            props.loadLocalRep(data, true, props.showOnlySenators)
                        }
                    />
                </CssTransition>
            )}
        </TransitionGroup>
    </Container>
);

CallDetails.propTypes = {
    ...SingleCallDetails.propTypes,
    ...SenatorsOrAllCallDetails.propTypes,
    actionIdentifier: PropTypes.string.isRequired,
    isMobileResolution: PropTypes.bool.isRequired,
    recipientName: PropTypes.string.isRequired,
    normalizedPhone: PropTypes.string.isRequired,
    onBackWithinCard: PropTypes.func.isRequired,
    onGoToNextCard: PropTypes.func.isRequired,
    handleSkippingEndCard: PropTypes.func.isRequired,
    onCallComplete: PropTypes.func.isRequired,
    actionCompletedTime: PropTypes.func.isRequired,
    apiRequestData: PropTypes.object.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    isPhoneLinkClicked: PropTypes.bool.isRequired,
    card: PropTypes.object.isRequired,
    isMultiActionsCard: PropTypes.bool.isRequired,
    onFindYourRep: PropTypes.func.isRequired,
    hideFindRepPopup: PropTypes.func.isRequired,
    showFindYouRepPopup: PropTypes.bool.isRequired,
    loadLocalRep: PropTypes.func.isRequired,
    thankYouText: PropTypes.string.isRequired,
    thankYouTitle: PropTypes.string.isRequired,
    localRepresentativeState: PropTypes.number.isRequired,
    isHouseRep: PropTypes.bool.isRequired,
    isSinglePerson: PropTypes.bool.isRequired,
    showOnlySenators: PropTypes.bool.isRequired,
    useLocalRepresentative: PropTypes.bool,
    skipFrontCard: PropTypes.bool,
    skipEndCard: PropTypes.bool,
    onContainerClick: PropTypes.func
};

const pictureUrl = state => {
    const picture = state.widget.pictures.find(
        a => a.recipientPictureId === state.widget.activeAction.recipientPictureId
    );
    return (picture || {}).pictureUrl;
};

export default connect(
    state => {
        const nextAction = getNextAction(
            state.widget.actions,
            state.widget.activeAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );

        return {
            nextAction,
            actionIdentifier: state.widget.activeAction.identifier,
            recipientName:
                state.widget.activeAction.localRepresentativeState > 0
                    ? state.localRep.localRepData.representative[0].lastName
                    : state.widget.activeAction.recipientMask,
            image: state.localRep.localRepData.representative
                ? state.localRep.localRepData.representative[0].photoUrl
                : '',
            pictureUrl: pictureUrl(state),
            isMobileResolution: state.widget.isMobileResolution,
            apiRequestData: state.widget.apiRequestData,
            phone:
                state.widget.activeAction.localRepresentativeState > 0
                    ? state.localRep.localRepData.representative[0].phone
                    : state.widget.activeAction.recipients,
            normalizedPhone:
                state.widget.activeAction.localRepresentativeState > 0
                    ? state.localRep.localRepData.representative[0].normalizedPhone
                    : normalizePhone(state.widget.activeAction.recipients),
            actionCompletedTime: getActionCompletedTime(state, 'call'),
            card: state.widget.cards[state.widget.activeAction.order - 1],
            cards: state.widget.cards,
            isMultiActionsCard:
                state.widget.cards[state.widget.activeAction.order - 1].type ===
                CardTypes.multipleActions,
            isMultiTargettedCard:
                state.widget.cards[state.widget.activeAction.order - 1].type ===
                CardTypes.targetedMultiAction,
            tips: (state.widget.activeAction.tips || '')
                .split(/(\r\n|\n|\r)/gm)
                .filter(t => t && t !== '\r\n' && t !== '\r' && t !== '\n'),
            isSubmitting: state.call.isSubmitting,
            isPhoneLinkClicked: state.call.isPhoneLinkClicked,
            showFindYouRepPopup: state.localRep.showFindYouRepPopup,
            thankYouText: state.widget.activeAction.thankYouText || '',
            thankYouTitle: state.widget.activeAction.thankYouTitle || '',
            isSpecifiedRecipient:
                state.widget.activeAction.localRepresentativeState === 0,
            isHouseRep: state.widget.activeAction.localRepresentativeState === 1,
            isSinglePerson: state.widget.activeAction.localRepresentativeState < 2,
            localRepresentativeState: state.widget.activeAction.localRepresentativeState,
            showOnlySenators: state.widget.activeAction.localRepresentativeState === 2,
            senatorsNotAvailable:
                state.localRep.localRepData.representative !== undefined
                    ? state.localRep.localRepData.representative.length < 2
                    : false,
            representatives: state.localRep.localRepData.representative
                ? state.localRep.localRepData.representative
                      .filter(function(a) {
                          return state.widget.activeAction.localRepresentativeState === 2
                              ? a.level === 2
                              : a.level === 2 || a.level === 1;
                      })
                      .map(a => ({
                          fullName:
                              state.widget.activeAction.localRepresentativeState === 2
                                  ? 'Sen ' +
                                    a.fullName +
                                    ' (' +
                                    a.partyIdentifier +
                                    '-' +
                                    state.localRep.localRepData.state +
                                    ')'
                                  : a.level === 2
                                  ? 'Sen ' +
                                    a.fullName +
                                    ' (' +
                                    a.partyIdentifier +
                                    '-' +
                                    state.localRep.localRepData.state +
                                    ')'
                                  : 'Rep ' +
                                    a.fullName +
                                    ' (' +
                                    state.localRep.localRepData.state +
                                    '-' +
                                    state.localRep.localRepData.district +
                                    ')',
                          partyIdentifier: a.partyIdentifier,
                          phone: a.phone,
                          normalizedPhone: a.normalizedPhone,
                          photoUrl: a.photoUrl || defaultImage
                      }))
                      .reverse()
                : [],
            useLocalRepresentative:
                state.widget.activeAction.localRepresentativeState > 0,
            skipFrontCard: state.widget.activeAction.skipFrontCard,
            skipEndCard: state.widget.activeAction.skipEndCard && !!nextAction,
            heading:
                state.widget.activeAction.customText ||
                copyFor('defaultText', 'heading', 'call'),
            oneSenatorAvailable:
                state.localRep.localRepData.representative !== undefined
                    ? state.localRep.localRepData.representative.length < 3
                    : false
        };
    },
    {
        onBackWithinCard,
        onGoToNextCard,
        handleSkippingEndCard,
        onCallComplete,
        onFindYourRep,
        hideFindRepPopup,
        loadLocalRep,
        onContainerClick
    }
)(CallDetails);



// WEBPACK FOOTER //
// ./src/containers/Call/CallDetails.jsx